import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { get, post, put, patch, del, isCancelError } from 'aws-amplify/api';

import { LoaderService } from '@app/data/services/loader.services';
import { getAuthHeaders } from './amplifyAuthHeaders';

type apiMethods = 'get' | 'post' | 'put' | 'patch' | 'del';

const amplifyApi = { get, post, put, patch, del };

@Injectable({
  providedIn: 'root',
})
export class APIWithLoaderHelper {
  private activeControllers: Map<number, AbortController> = new Map();
  private requestIdCounter = 0;

  constructor(public loaderService: LoaderService) {}

  /**
   * Abort all active requests and clear controllers.
   *
   * Attempts to abort all active requests being tracked by this helper.
   * Logs a summary of the abort attempts, including any failures.
   * Ensures the loader is hidden after cancellation.
   */
  cancelAll(): void {
    try {
      const controllers = Array.from(this.activeControllers.entries());
      const total = controllers.length;
      let abortedCount = 0;
      const failedIds: number[] = [];

      for (const [id, ctrl] of controllers) {
        try {
          ctrl.abort();
          abortedCount += 1;
        } catch (_e) {
          failedIds.push(id);
        }
      }

      console.debug(`[APIWithLoaderHelper.cancelAll]: abort summary`, {
        total,
        aborted: abortedCount,
        failedIds: failedIds.length ? failedIds : undefined,
      });
    } finally {
      this.activeControllers.clear();
      // Ensure loader is hidden when cancelling requests
      this.loaderService.hide();
    }
  }

  /**
   * Helper function to make API requests with loader management.
   * @param path The API endpoint path.
   * @param method The HTTP method to use.
   * @param params The request parameters.
   * @param showLoader Whether to show the loader during the request.
   * @param label An optional label for the request (used for logging).
   * @returns An Observable of the API response.
   */
  private doRequest<T = any>(
    path: string,
    method: apiMethods,
    params: any = {},
    showLoader: boolean = true,
    label?: string,
    responseType: 'json' | 'text' = 'json'
  ): Observable<T> {
    if (showLoader) this.loaderService.show();

    const controller = new AbortController();
    const requestId = ++this.requestIdCounter;
    this.activeControllers.set(requestId, controller);
    console.debug(`[APIWithLoaderHelper.doRequest]: starting ${method.toUpperCase()} ${path}`, {
      requestId,
      activeControllers: this.activeControllers.size,
      label,
    });

    const { body, headers, queryStringParameters, ...rest } = params ?? {};

    const promise: Promise<T> = (async (): Promise<T> => {
      const authHeaders = await getAuthHeaders();
      if (!authHeaders) {
        // getAuthHeaders already redirected to /login.
        throw new Error('No authenticated session');
      }

      const options: any = { ...rest };
      options.headers = { ...(headers ?? {}), ...authHeaders };
      if (body !== undefined) {
        options.body = body;
      }
      if (queryStringParameters !== undefined) {
        options.queryParams = queryStringParameters;
      }

      const operation = amplifyApi[method]({ apiName: 'api', path, options });

      // NOTE: deliberately NOT wiring `controller.signal` -> `operation.cancel()`.
      // aws-amplify v6's `cancel()` genuinely aborts the underlying fetch, whereas
      // the old v4 `signal` option was silently ignored. `filterService.setFilters`
      // calls `cancelAll()` during the site-change flow, which would otherwise abort
      // in-flight requests (map layers / environmental data sources) and surface
      // spurious `CanceledError`s. Preserve the previous (inert) behaviour.
      try {
        const { body: responseBody } = await operation.response;
        const data = (responseType === 'text' ? await responseBody.text() : await responseBody.json()) as unknown as T;
        console.debug(`[APIWithLoaderHelper.doRequest]: success ${method.toUpperCase()} ${path}`, { requestId, label });
        return data;
      } catch (err: any) {
        if (isCancelError(err)) {
          console.debug(`[APIWithLoaderHelper.doRequest]: request aborted ${method.toUpperCase()} ${path}`, {
            requestId,
            label,
          });
        } else {
          console.debug(`[APIWithLoaderHelper.doRequest]: error ${method.toUpperCase()} ${path}`, {
            requestId,
            label,
            error: err,
          });
        }
        // propagate error (including AbortError)
        throw err;
      } finally {
        this.activeControllers.delete(requestId);
        console.debug(`[APIWithLoaderHelper.doRequest]: finished ${method.toUpperCase()} ${path}`, {
          requestId,
          label,
          activeControllers: this.activeControllers.size,
        });
        if (showLoader) {
          this.loaderService.hide();
        }
      }
    })();

    return from<Promise<T>>(promise);
  }

  get<T>(path: string, params: object = {}, showLoader: boolean = true, label?: string): Observable<T> {
    return this.doRequest(path, 'get', params, showLoader, label);
  }

  /**
   * Perform a GET request and return the raw text body (e.g. HTML or CSV).
   * The default `get` parses the response as JSON; use this for endpoints that
   * return a non-JSON payload.
   */
  getText<T = string>(path: string, params: object = {}, showLoader: boolean = true, label?: string): Observable<T> {
    return this.doRequest(path, 'get', params, showLoader, label, 'text');
  }

  post<T>(
    path: string,
    body: object | any,
    params: object = {},
    showLoader: boolean = true,
    headers: any = {},
    label?: string
  ): Observable<T> {
    return this.doRequest(path, 'post', { ...params, body, headers }, showLoader, label);
  }

  patch<T>(path: string, body: object, params: object = {}, showLoader: boolean = true, label?: string): Observable<T> {
    return this.doRequest(path, 'patch', { ...params, body }, showLoader, label);
  }

  put<T>(path: string, body: object, params: object = {}, showLoader: boolean = true, label?: string): Observable<T> {
    return this.doRequest(path, 'put', { ...params, body }, showLoader, label);
  }

  del<T>(path: string, params: object = {}, showLoader: boolean = true, label?: string): Observable<T> {
    return this.doRequest(path, 'del', params, showLoader, label);
  }
}
